import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useState, useEffect } from "react";

interface QuantityButtonProps {
  initialValue?: number;
  min?: number;
  max?: number;
  onChange?: (value: number) => void;
  className?: string;
}

export default function QuantityButton({
  initialValue = 1,
  min = 1,
  max = 100,
  onChange,
  className = "",
}: QuantityButtonProps) {
  const [value, setValue] = useState(initialValue);

  // Update internal state if initialValue changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const updateValue = (newValue: number) => {
    // Ensure the value stays within min/max bounds
    const clampedValue = Math.min(Math.max(newValue, min), max);
    setValue(clampedValue);
    onChange?.(clampedValue);
  };

  const increment = () => updateValue(value + 1);
  const decrement = () => updateValue(value - 1);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10) || min;
    updateValue(newValue);
  };

  return (
    <div className={`flex items-center border border-gray-300 rounded-md ${className}`}>
      <button
        type="button"
        onClick={decrement}
        disabled={value <= min}
        className="h-10 w-10 rounded-l-md flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Decrease quantity"
      >
        <MinusIcon className="h-5 w-5" />
      </button>
      
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={handleInputChange}
        className="w-10 text-center border-0 p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus:outline-none focus:ring-0"
        aria-label="Quantity"
      />
      
      <button
        type="button"
        onClick={increment}
        disabled={value >= max}
        className="h-10 w-10 rounded-r-md flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Increase quantity"
      >
        <PlusIcon className="h-5 w-5 text-brand-500" />
      </button>
    </div>
  );
}
